import { useQuery } from '@tanstack/react-query';
import {
  searchAssets,
  searchCategories,
} from '@vimond/services/content-discovery';
import { getEnvVar } from '@vimond/utils/env';
import { useAuthentication } from '../../authentication';
import { hasAccess } from '../../authentication/filterContent';
import { getProductIds } from '../../authentication/getProductIds';

// Helper function to escape special characters in search queries
const escapeSearchQuery = (query: string): string => {
  return query.replace(/[+\-&|!(){}[\]^"~*?:\\]/g, '\\$&');
};

export const useSearch = (query: string, locale?: string, encode?: boolean) => {
  const isSingleTerm = !!query && query.trim().split(/\s+/).length === 1;
  const sanitizedQuery = query ? escapeSearchQuery(query.trim()) : '';

  const formattedQuery = sanitizedQuery
    ? [
        isSingleTerm
          ? `title:${sanitizedQuery}~^5`
          : `title:"${sanitizedQuery}"^5`,
        isSingleTerm
          ? `keywords:${sanitizedQuery}~^3`
          : `keywords:"${sanitizedQuery}"^3`,
        isSingleTerm
          ? `labels:${sanitizedQuery}~^3`
          : `labels:"${sanitizedQuery}"^3`,
        isSingleTerm
          ? `description-short:${sanitizedQuery}~`
          : `description-short:"${sanitizedQuery}"`,
      ].join(' OR ')
    : '';

  const {
    data: assets,
    error: assetsErrors,
    isLoading: assetsAreLoading,
  } = useQuery({
    queryKey: ['search', 'assets', query],
    queryFn: () => searchAssets(formattedQuery, locale, encode),
    enabled: !!query,
  });

  const {
    data: categories,
    error: categoriesErrors,
    isLoading: categoriesAreLoading,
  } = useQuery({
    queryKey: ['search', 'categories', query],
    queryFn: () => searchCategories(query),
    enabled: !!query,
  });

  if (getEnvVar('NEXT_PUBLIC_SHOW_INACCESSIBLE_CONTENT') === 'false') {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { jwt: token } = useAuthentication();
    const products = getProductIds(token ?? '');
    return {
      assets: assets?.filter((asset) =>
        hasAccess(asset.products ?? [], products, !!token),
      ),
      isLoading: assetsAreLoading || categoriesAreLoading,
      assetsErrors,
      categories: categories?.filter((asset) =>
        hasAccess(asset.products ?? [], products, !!token),
      ),
      categoriesErrors,
    };
  }

  return {
    assets,
    isLoading: assetsAreLoading || categoriesAreLoading,
    assetsErrors,
    categories,
    categoriesErrors,
  };
};
